import { Injectable } from '@angular/core';
import { TrendsSubjectColorResolver } from './trends-subject-color-resolver';
import { CvdChartData, CvdChartDataValue, CvdBandDataValue } from '@covance/ng-cvd3';
import { LabTestResult } from '../lab-test-results';
import { chain, isEmpty } from 'lodash';
import { getShapeByGender, getFlaggedClassName } from './chart-helper';

export interface LabTrendsChartDataValue extends CvdChartDataValue {
    colorClassName: string;
}

@Injectable()
export class TrendsChartDataBuilder {
    constructor(private colorResolver: TrendsSubjectColorResolver) { }

    public buildChartData(results: LabTestResult[]): CvdChartData<LabTrendsChartDataValue>[] {
        if (results === null) {
            return null;
        }
        
        const cvdData = chain(results)
            .groupBy(res => res.ctSubjectId)
            .map((bySubjectId: LabTestResult[], subjectId: number) => {
                const values = chain(bySubjectId)
                    .filter(res => res.currentResult && res.currentResult.numericResult !== null)
                    .sortBy(value => value.visitCollectionDateValue)
                    .map(res => {
                        return {
                            x: res.dayOfStudy,
                            y: res.currentResult.numericResult,
                            rows: [res.labTestId],
                            series: 0,
                            shape: getShapeByGender(res.sex),
                            colorClassName: getFlaggedClassName(res)
                        } as LabTrendsChartDataValue;
                    })
                    .value();

                const color = this.getLineColor(bySubjectId);

                const data: CvdChartData<LabTrendsChartDataValue> = {
                    key: `SubjectId: ${subjectId}`,
                    rows: [],
                    color: color,
                    values: values
                };

                return data;
            }).value();

        return cvdData;
    }

    public buildBandData(results: LabTestResult[]): CvdChartData<CvdBandDataValue>[] {
        const cvdData = chain(results)
        .groupBy(res => res.ctSubjectId)
        .map((bySubjectId: LabTestResult[], subjectId: string) => {
            const values = chain(bySubjectId)
                .filter(res => res.currentResult && res.currentResult.numericResult !== null)
                .filter(res => res.currentResult.hasValidNumericRange)
                .sortBy(value => value.visitCollectionDateValue)
                .map(res => {
                    return {
                        x: res.dayOfStudy,
                        y1: res.currentResult.min,
                        y2: res.currentResult.max,
                        rows: [],
                        series: 0
                    } as CvdBandDataValue;
                })
                .value();
            const color = this.getLineColor(bySubjectId);
            const data: CvdChartData<CvdBandDataValue> = {
                key: `SubjectId: ${subjectId}`,
                rows: [],
                color: color,
                values: values
            };
            return data;
        }).value();

        return cvdData;
    }

    private getLineColor(results: LabTestResult[]) {

        if (isEmpty(results)) {
            return '';
        }

        const index = results[0].subjectSerialNumber;
        return this.colorResolver.getColor(index);
    }
}